// @ts-nocheck
import {Vector3} from 'three';
import type {ColorRepresentation} from 'three';
import * as THREE from 'three/webgpu';
import {
    attribute,
    cameraProjectionMatrix,
    cameraViewMatrix,
    dashSize,
    float,
    gapSize,
    int,
    Loop,
    materialLineDashOffset,
    materialLineDashSize,
    materialLineGapSize,
    modelWorldMatrix,
    uint,
    uniform,
    uniformArray,
    vec2,
    vec4,
    viewportSize,
} from 'three/tsl';
import {createLineDistancePercentages} from './utils/createLineDistancePercentages';


/** 屏幕空间虚线距离百分比 attribute 名称。 */
export const SCREEN_SPACE_DASHED_LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME = 'lineDistancePercent';

/** 屏幕空间虚线材质默认值来源。 */
const DEFAULT_VALUES = new THREE.LineDashedMaterial();

/**
 * 屏幕空间虚线折线点值。
 */
export type ScreenSpaceDashedLinePointValue = Vector3 | readonly [number, number, number];

/**
 * 屏幕空间虚线材质参数。
 */
export interface ScreenSpaceDashedLineNodeMaterialParameters extends THREE.NodeMaterialParameters {
    /** 材质颜色。 */
    color?: ColorRepresentation;
    /** 屏幕空间虚线段长度。 */
    dashSize?: number;
    /** 屏幕空间虚线间隔长度。 */
    gapSize?: number;
    /** 线宽。 */
    linewidth?: number;
    /** 材质透明度。 */
    opacity?: number;
    /** 是否透明。 */
    transparent?: boolean;
    /** 折线点列。 */
    points?: ScreenSpaceDashedLinePointValue[];
}

/**
 * 归一化屏幕空间虚线点。
 * @param point 输入点。
 * @returns 克隆后的 Vector3 点。
 */
function normalizeScreenSpaceDashedLinePoint(point: ScreenSpaceDashedLinePointValue): Vector3 {
    if (point instanceof Vector3) {
        return point.clone();
    }

    return new Vector3(point[0], point[1], point[2]);
}

/**
 * 屏幕空间虚线 Node 材质。
 * 说明：
 * 1. 参考 `ThinChainLIneNodeMaterial` 的屏幕投影距离计算方式；
 * 2. 该材质只表达纯虚线，不包含点线点混合节奏；
 * 3. 每条线应独享一个材质实例，避免不同折线互相覆盖 `points` uniform。
 */
export class ScreenSpaceDashedLineNodeMaterial extends THREE.NodeMaterial {
    /** 运行时材质标记，用于测试与调试识别。 */
    public readonly isScreenSpaceDashedLineNodeMaterial: boolean = true;

    /** 屏幕空间虚线段长度。 */
    public dashSize: number = 7;

    /** 屏幕空间虚线间隔长度。 */
    public gapSize: number = 4;

    /** 当前折线点列。 */
    private _points: Vector3[] = [];

    /** 折线点列 uniform。 */
    private pointsUniformNode: ReturnType<typeof uniformArray> | null = null;

    /** 折线点数量 uniform。 */
    private readonly pointsLengthUniform = uniform(uint(0));

    /**
     * 设置折线点列。
     * @param value 新点列。
     */
    public set points(value: ScreenSpaceDashedLinePointValue[]) {
        const normalizedPoints = value.map((point) => normalizeScreenSpaceDashedLinePoint(point));
        const hasValidPolyline = normalizedPoints.length >= 2;
        const nextPoints = hasValidPolyline ? normalizedPoints : [];

        this.pointsLengthUniform.value = nextPoints.length;

        if (this.pointsUniformNode && this._points.length === nextPoints.length) {
            this._points = nextPoints;
            this.pointsUniformNode.array = this._points;
            return;
        }

        this._points = nextPoints;

        if (hasValidPolyline) {
            this.pointsUniformNode = uniformArray(this._points, 'vec3');
            this.needsUpdate = true;
            return;
        }

        const hadPointsUniformNode = this.pointsUniformNode !== null;
        this.pointsUniformNode = null;

        if (hadPointsUniformNode) {
            this.needsUpdate = true;
        }
    }

    /**
     * 获取折线点列。
     * @returns 当前折线点列。
     */
    public get points(): Vector3[] {
        return this._points;
    }

    /**
     * 构造屏幕空间虚线 Node 材质。
     * @param parameters 材质参数。
     */
    public constructor(parameters: ScreenSpaceDashedLineNodeMaterialParameters = {}) {
        super();

        this.setDefaultValues(DEFAULT_VALUES);
        this.dashSize = parameters.dashSize ?? this.dashSize;
        this.gapSize = parameters.gapSize ?? this.gapSize;

        if (parameters.points) {
            this.points = parameters.points;
        }

        const {points: _points, ...materialParameters} = parameters;
        void _points;

        this.setValues(materialParameters);
    }

    /**
     * 构建屏幕空间虚线节点变体。
     */
    public setupVariants(): void {
        if (!this.pointsUniformNode) {
            return;
        }

        const lineDistancePercentNode = attribute(
            SCREEN_SPACE_DASHED_LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME,
        );

        const worldToScreen = (worldPositionNode) => {
            const clipNode = cameraProjectionMatrix.mul(
                cameraViewMatrix.mul(vec4(worldPositionNode, 1.0)),
            );
            const ndcNode = clipNode.xy.div(clipNode.w);

            return vec2(
                ndcNode.x.mul(0.5).add(0.5).mul(viewportSize.x),
                ndcNode.y.mul(-0.5).add(0.5).mul(viewportSize.y),
            );
        };

        const activePointsUniformNode = this.pointsUniformNode;
        const pointsLengthNode = int(this.pointsLengthUniform);
        const totalScreenDistanceNode = float(0).toVar('totalScreenDistance');
        const segmentCountNode = pointsLengthNode.sub(int(1)).toVar('segmentCount');

        // 按屏幕投影后的长度累计整条折线距离，让虚线节奏不受相机缩放影响。
        Loop(segmentCountNode, ({i}) => {
            const segmentStartNode = activePointsUniformNode.element(i);
            const segmentEndNode = activePointsUniformNode.element(i.add(int(1)));
            const segmentStartWorldNode = (
                modelWorldMatrix.mul(vec4(segmentStartNode, 1.0))
            ).xyz;
            const segmentEndWorldNode = (
                modelWorldMatrix.mul(vec4(segmentEndNode, 1.0))
            ).xyz;
            const segmentStartScreenNode = worldToScreen(segmentStartWorldNode);
            const segmentEndScreenNode = worldToScreen(segmentEndWorldNode);
            const segmentScreenDistanceNode = float(
                segmentStartScreenNode.distance(segmentEndScreenNode),
            ).toVar('segmentScreenDistance');

            totalScreenDistanceNode.addAssign(segmentScreenDistanceNode);
        });

        const lineDistanceNode = totalScreenDistanceNode.mul(lineDistancePercentNode);
        const dashSizeNode = materialLineDashSize;
        const gapSizeNode = materialLineGapSize;
        const offsetNode = materialLineDashOffset;

        dashSize.assign(dashSizeNode);
        gapSize.assign(gapSizeNode);

        const lineDistanceWithOffsetNode = lineDistanceNode.add(offsetNode);
        const cycleSizeNode = dashSize.add(gapSize);
        const positionNode = float(lineDistanceWithOffsetNode.mod(cycleSizeNode)).toVar('cyclePosition');

        // 超过 dash 段后进入 gap，直接丢弃片元形成纯虚线。
        positionNode
            .greaterThan(dashSize)
            .discard();
    }
}

/**
 * 判断目标值是否为屏幕空间虚线 Node 材质。
 * @param value 待判断值。
 * @returns 当目标值为屏幕空间虚线 Node 材质时返回 true。
 */
export function isScreenSpaceDashedLineNodeMaterial(value: unknown): value is ScreenSpaceDashedLineNodeMaterial {
    return Boolean(value)
        && typeof value === 'object'
        && Reflect.get(value, 'isScreenSpaceDashedLineNodeMaterial') === true;
}

/**
 * 从位置 attribute 提取屏幕空间虚线点列。
 * @param positionAttribute 位置 attribute。
 * @returns 折线点列。
 */
function createScreenSpaceDashedLinePointsFromPositionAttribute(
    positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): Vector3[] {
    const points: Vector3[] = [];

    for (let index = 0; index < positionAttribute.count; index += 1) {
        points.push(new Vector3().fromBufferAttribute(positionAttribute, index));
    }

    return points;
}

/**
 * 同步屏幕空间虚线材质依赖的几何数据。
 * @param line 待同步的线对象。
 */
export function syncScreenSpaceDashedLineGeometry(line: THREE.Line): void {
    line.computeLineDistances();

    const positionAttribute = line.geometry.getAttribute('position');
    const targetMaterial = Array.isArray(line.material)
        ? line.material[0]
        : line.material;
    if (positionAttribute && isScreenSpaceDashedLineNodeMaterial(targetMaterial)) {
        targetMaterial.points = createScreenSpaceDashedLinePointsFromPositionAttribute(positionAttribute);
    }

    const lineDistanceAttribute = line.geometry.getAttribute('lineDistance');
    if (!lineDistanceAttribute) {
        line.geometry.deleteAttribute(SCREEN_SPACE_DASHED_LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME);
        return;
    }

    line.geometry.setAttribute(
        SCREEN_SPACE_DASHED_LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME,
        new THREE.Float32BufferAttribute(
            createLineDistancePercentages(lineDistanceAttribute),
            1,
        ),
    );
}

